import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't reset password. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex" data-testid="page-reset-password">
      <div className="hidden lg:block lg:w-1/2 bg-sage relative">
        <div className="absolute inset-0 flex items-center justify-center p-16">
          <div className="text-cream max-w-md">
            <div className="font-serif-display text-5xl leading-[1.05]">
              Choose a new password.
            </div>
            <p className="mt-6 text-cream/70 leading-relaxed">
              Make it something you'll remember — but not something a stranger could guess. At least 8 characters.
            </p>
          </div>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="font-serif-display text-2xl text-ink" data-testid="reset-logo">
            Pip<span className="text-gold italic"> Bennett</span>
          </Link>

          {!token ? (
            <div className="mt-12" data-testid="reset-no-token">
              <h1 className="font-serif-display text-3xl text-ink leading-tight">This reset link looks broken.</h1>
              <p className="mt-5 text-ink-soft leading-relaxed">
                Try requesting a fresh one — they're valid for one hour.
              </p>
              <Link
                to="/forgot-password"
                className="mt-10 inline-flex items-center gap-2 text-sage hover:text-sage-dark font-medium"
                data-testid="reset-request-new"
              >
                Request a new link
                <ArrowRight size={16} strokeWidth={1.8} />
              </Link>
            </div>
          ) : done ? (
            <div className="mt-12" data-testid="reset-success">
              <div className="inline-flex w-12 h-12 items-center justify-center rounded-full bg-sage text-cream mb-6">
                <Check size={20} strokeWidth={1.5} />
              </div>
              <h1 className="font-serif-display text-4xl text-ink leading-tight">Password updated.</h1>
              <p className="mt-5 text-ink-soft leading-relaxed">
                You're being taken to sign in now…
              </p>
            </div>
          ) : (
            <>
              <h1 className="font-serif-display text-4xl text-ink mt-10 leading-tight">Set a new password.</h1>
              <p className="mt-3 text-ink-soft">At least 8 characters.</p>

              <form onSubmit={submit} className="mt-10 space-y-5" data-testid="reset-form">
                <div>
                  <label className="eyebrow block mb-2 text-ink-soft">New password</label>
                  <input
                    required
                    type="password"
                    autoComplete="new-password"
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-white border border-hairline rounded-full px-6 py-4 text-ink focus:outline-none focus:border-sage transition"
                    data-testid="reset-password"
                  />
                </div>
                <div>
                  <label className="eyebrow block mb-2 text-ink-soft">Confirm new password</label>
                  <input
                    required
                    type="password"
                    autoComplete="new-password"
                    minLength={8}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="w-full bg-white border border-hairline rounded-full px-6 py-4 text-ink focus:outline-none focus:border-sage transition"
                    data-testid="reset-confirm"
                  />
                </div>
                {error && (
                  <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3" data-testid="reset-error">
                    {error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-sage hover:bg-sage-dark text-cream rounded-full px-8 py-4 font-medium tracking-wide transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="reset-submit"
                >
                  {loading ? "Saving…" : "Save new password"}
                  {!loading && <ArrowRight size={16} strokeWidth={1.8} />}
                </button>
              </form>

              <p className="mt-8 text-sm text-ink-soft">
                <Link to="/login" className="text-sage hover:text-sage-dark font-medium" data-testid="reset-to-login">
                  Back to sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
